import { useRequiredContext } from "@redotech/react-util/context";
import { createContext, MutableRefObject, ReactNode, useRef } from "react";
import type { AskRedoUIMessage } from "../types";

/**
 * Holds a live ref to the chat's current `messages` array so deeply-
 * nested tool-result components (like the escalate-to-human card,
 * rendered inside `renderParts`) can snapshot the conversation at
 * click time without us threading `messages` through every component
 * in the chain.
 *
 * A ref (not state) so the card never re-renders just because a
 * sibling delta arrived — it just needs the latest snapshot when the
 * user clicks "Yes, connect me".
 */
export const ChatMessagesRefContext = createContext<
  MutableRefObject<AskRedoUIMessage[]> | undefined
>(undefined);

export function useChatMessagesRef(): MutableRefObject<AskRedoUIMessage[]> {
  return useRequiredContext(ChatMessagesRefContext);
}

export function ChatMessagesRefProvider({
  messages,
  children,
}: {
  messages: AskRedoUIMessage[];
  children: ReactNode;
}) {
  const ref = useRef<AskRedoUIMessage[]>(messages);
  ref.current = messages;
  return (
    <ChatMessagesRefContext.Provider value={ref}>
      {children}
    </ChatMessagesRefContext.Provider>
  );
}
